import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from 'sonner';
import { AuthProvider } from '@/contexts/AuthContext';
import { AuthGuard } from '@/components/AuthGuard';
import { AppLayout } from '@/components/AppLayout';
// Pages
import Login from '@/pages/Login';
import Unauthorized from '@/pages/Unauthorized';
import NotFound from '@/pages/NotFound';
import Dashboard from '@/pages/Dashboard';
import Inventory from '@/pages/Inventory';
import Lookup from '@/pages/Lookup';
import SalesPage from '@/pages/Sales';
import BranchesPage from '@/pages/Branches';
import ItemsPage from '@/pages/Items';
import UsersPage from '@/pages/Users';
import SettingsPage from '@/pages/Settings';
import SuppliersPage from '@/pages/Suppliers';
import SupplierInvoiceNewPage from '@/pages/SupplierInvoiceNew';
import ActivityLogs from '@/pages/ActivityLogs';
import Reports from '@/pages/Reports'; // Added import
import ExpensesPage from '@/pages/Expenses';
import AccountsPage from '@/pages/Accounts';
import LayawaysPage from '@/pages/Layaways';

const queryClient = new QueryClient();

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AuthProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Protected routes (requires login) */}
            <Route element={<AuthGuard />}>
              <Route element={<AppLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="dashboard" element={<Navigate to="/" replace />} />
                <Route path="inventory" element={<Inventory />} />
                <Route path="sales" element={<SalesPage />} />
                <Route path="layaways" element={<LayawaysPage />} />
                <Route path="expenses" element={<ExpensesPage />} />
                <Route path="accounts" element={<AccountsPage />} />
                <Route path="settings" element={<SettingsPage />} />

                {/* Branch-only route */}
                <Route element={<AuthGuard requiredRole="branch" />}>
                  <Route path="lookup" element={<Lookup />} />
                </Route>

                {/* Admin-only routes */}
                <Route element={<AuthGuard requiredRole="admin" />}>
                  <Route path="branches" element={<BranchesPage />} />
                  <Route path="items" element={<ItemsPage />} />
                  <Route path="users" element={<UsersPage />} />
                  <Route path="suppliers" element={<SuppliersPage />} />
                  <Route path="suppliers/invoices/new" element={<SupplierInvoiceNewPage />} />
                  <Route path="activity-logs" element={<ActivityLogs />} />
                  <Route path="reports" element={<Reports />} />
                </Route>

                {/* 404 inside protected layout */}
                <Route path="*" element={<NotFound />} />
              </Route>
            </Route>

            {/* Fallback for any unknown route not caught above */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}
